import { applyColumnOrder, ColumnDef } from '../../../components/organisms/ShipmentTable';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { shipmentListColumnSelectionItems } from '../../../constants/shipment';

/**
 * The Shipments table's column defaults — which columns show, how wide, which
 * stretch, and the header labels that differ from the field name.
 *
 * Split out of `shipmentListFilters` because they are not filters: that file
 * describes what the user narrows the list by, this one describes how the list
 * is laid out, and keeping both in one module pushed it past the 250-line
 * limit once the Shipment Type slot was added.
 */

const DEFAULT_VISIBLE_FIELDS = [
  'id',
  'shipmentTypeCode',
  'shipmentStatusCode',
  'createdAt',
  'sourceName',
  'destinationName',
  'clientContractNames',
  'connoteNo',
  'qtyTotal',
];

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  id: 90,
  shipmentTypeCode: 120,
  shipmentStatusCode: 110,
  qtyTotal: 90,
  qtyReceived: 100,
  qtyPending: 100,
  shippingCost: 100,
};

const DEFAULT_COLUMN_FLEX: Record<string, number> = {
  sourceName: 2,
  destinationName: 2,
  clientContractNames: 2,
  notes: 2,
};

const DEFAULT_COLUMN_MIN_WIDTHS: Record<string, number> = {
  sourceName: 160,
  destinationName: 160,
  clientContractNames: 160,
  notes: 160,
  latestTrackingStatus: 160,
  courierName: 140,
  connoteNo: 140,
  createdAt: 130,
  updatedAt: 130,
  completedAt: 130,
  arrivalDate: 130,
  createdByName: 140,
  updatedByName: 140,
  sourceNodeType: 140,
  destinationNodeType: 140,
  referenceNumber1: 120,
  referenceNumber2: 120,
  referenceNumber3: 120,
};

const DEFAULT_COLUMN_HEADER_LABELS: Record<string, string> = {
  id: 'ID',
  shipmentTypeCode: 'Type',
  shipmentStatusCode: 'Status',
  clientContractNames: 'Client/Contract',
};

export const buildInitialColumnSelection = (): ColumnSelectionItem[] =>
  shipmentListColumnSelectionItems.map((group) => ({
    ...group,
    value: group.value.map((col) => ({
      ...col,
      value: DEFAULT_VISIBLE_FIELDS.includes(col.field),
    })),
  }));

export const buildColumnDefs = (): ColumnDef[] => {
  const defs: ColumnDef[] = [];
  for (const group of shipmentListColumnSelectionItems) {
    for (const col of group.value) {
      // printLabel is handled as a row-level action, not a column cell
      if (col.field === 'printLabel') continue;
      defs.push({
        key: col.field,
        label: col.title,
        group: group.title,
        visible: DEFAULT_VISIBLE_FIELDS.includes(col.field),
        ...(DEFAULT_COLUMN_HEADER_LABELS[col.field] !== undefined
          ? { headerLabel: DEFAULT_COLUMN_HEADER_LABELS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_WIDTHS[col.field] !== undefined
          ? { width: DEFAULT_COLUMN_WIDTHS[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_FLEX[col.field] !== undefined
          ? { flex: DEFAULT_COLUMN_FLEX[col.field] }
          : {}),
        ...(DEFAULT_COLUMN_MIN_WIDTHS[col.field] !== undefined
          ? { minWidth: DEFAULT_COLUMN_MIN_WIDTHS[col.field] }
          : {}),
      });
    }
  }
  return applyColumnOrder(
    defs,
    DEFAULT_VISIBLE_FIELDS.map((field) => ({ field }))
  );
};
